import {
    EVENT_TYPES,
    ICallback,
    IPromptCallback,
    ISmsData,
    SMS_DEFAULT_TIME_FOR_MONITOR,
    SMS_STATUS_CHECK_FAILED_ERROR,
    SmsError,
    SmsErrorLevel,
    WHATSAPP_POLLING_DURING_PROMPT_ATTEMPTS,
    WHATSAPP_POLLING_INTERVAL
} from './ts-sms.settings';
import get from 'lodash/get';
import {Gateways} from '@techsee/techsee-common/lib/helpers/sms-delivery';
import {ISmsSettings} from '../../models/AccountSettings';
import {EventLogConstant} from '../../constants/events/event-log.constant';
import {Event} from './SmsServiceFlowEvent';
import {ISmsFlowLogger} from '../../states/invite-new/invite.flow.logger';
import {sleep} from '../../../../../__tests__/utils/async-utils';
import {SmsDeliveryStatus} from '@techsee/techsee-common/lib/constants/sms.constants';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {SmsStatusParams} from '../Session/SessionContracts';
import {ISmsService} from '@techsee/techsee-client-infra/lib/services/smsService';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';

export class SendFullSmsFlowService {
    private _roomService: IRoomService;

    private _smsService: ISmsService;

    private _smsServiceLogger: ISmsFlowLogger;

    private _smsSettings: ISmsSettings;

    private whatsAppDeliveredStatus = ['delivered', 'read'];

    private whatsAppFailedStatus = ['rejected', 'undeliverable'];

    private whatsAppPendingStatus = ['submitted'];

    private monitorDeliveryTimeout: ReturnType<typeof setTimeout> | null = null;

    private isClientLoaded: boolean = false;

    private lastSmsStatus: string = '';

    private _browserUtils: IBrowserUtilsService;

    private currentGateway: string = '';

    private smsTimeForMonitor = SMS_DEFAULT_TIME_FOR_MONITOR;

    constructor(
        smsService: ISmsService,
        roomService: IRoomService,
        smsSettings: ISmsSettings,
        eventLog: ISmsFlowLogger,
        browserUtils: IBrowserUtilsService
    ) {
        this._smsService = smsService;
        this._roomService = roomService;
        this._smsSettings = smsSettings;
        this._smsServiceLogger = eventLog;
        this._updateSmsError = this._updateSmsError.bind(this);
        this._browserUtils = browserUtils;
    }

    async sendSMS(
        smsData: ISmsData,
        progressCb: ICallback,
        allowPrompt: boolean,
        promptCb: IPromptCallback | undefined,
        allowSendByWhatsApp: boolean
    ): Promise<any> {
        const {messageId, success} = await this._tryWhatsApp(
            smsData,
            progressCb,
            allowSendByWhatsApp,
            this._smsServiceLogger
        );

        if (success) {
            return {messageId, success: true, isWhatsApp: true};
        }

        const smsResult = await this._sendSms(smsData, progressCb);

        if (smsResult && smsResult.success === false) {
            return smsResult;
        }
        const timeOfMonitor = Date.now();
        this._browserUtils.saveToSessionStorage('smsMonitorTime', timeOfMonitor);

        // Wait for delivery status before returning
        const deliveryResult = await this.monitorSmsDelivery(smsData.roomId, progressCb);

        return deliveryResult;
    }

    private _handleProgressUpdate(
        roomId: string,
        progressCb: ICallback,
        gateway: string = '',
        clientReceived = false,
        status?: SmsDeliveryStatus
    ): {success: boolean; clientConnected?: boolean} | void {
        if (clientReceived) {
            this.isClientLoaded = true;
            this._clearMonitorTimeout();
            this.emitProgress(new Event(EVENT_TYPES.CLIENT_RECEIVED, {}), progressCb);
            return {success: true, clientConnected: true};
        }
        if (status) {
            let eventType = this.getSmsEventType(status);
            if (eventType === EVENT_TYPES.ERROR) {
                this._updateSmsError(
                    gateway,
                    new SmsError('INVITE.CONTROLLER.SMS_CANNOT_BE_SENT_TEMP', SmsErrorLevel.INFO),
                    '',
                    progressCb
                );
            } else if (eventType) {
                this.emitProgress(new Event(eventType, {}), progressCb);
            }
            this.lastSmsStatus = status;
            if (status === SmsDeliveryStatus.DELIVERED) {
                return this._smsProcessCompleted(roomId, gateway, clientReceived);
            }
        }
    }

    async smsStatusChanged(data: SmsStatusParams, progressCb: ICallback): Promise<void> {
        if (data.isClientLoaded) {
            if (!this.isClientLoaded) {
                this._handleProgressUpdate(data.roomId, progressCb, '', true);
                this._clearMonitorTimeout();
                this.isClientLoaded = true;
            }
            return;
        }
        switch (data.notificationType) {
            case SmsDeliveryStatus.SENDING:
                this.currentGateway = data?.gateway || '';
                break;
            case SmsDeliveryStatus.DELIVERED:
                if (!this.isClientLoaded && this.lastSmsStatus !== SmsDeliveryStatus.DELIVERED) {
                    this._handleProgressUpdate(
                        data.roomId,
                        progressCb,
                        data.gateway,
                        false,
                        SmsDeliveryStatus.DELIVERED
                    );
                }
                break;
            case SmsDeliveryStatus.FAILED:
                if (this.lastSmsStatus !== SmsDeliveryStatus.FAILED) {
                    this._clearMonitorTimeout();
                    this._handleProgressUpdate(data.roomId, progressCb, data.gateway, false, SmsDeliveryStatus.FAILED);
                }
                break;
            case SmsDeliveryStatus.RESEND:
                if (!this.isClientLoaded && this.lastSmsStatus !== SmsDeliveryStatus.RESEND) {
                    this.emitProgress(
                        new Event(EVENT_TYPES.RESENDING_SMS, {statusMessage: 'INVITE.CONTROLLER.RESENDING_SMS'}),
                        progressCb
                    );
                    this.currentGateway = data?.gateway || '';

                    this.lastSmsStatus = SmsDeliveryStatus.RESEND;
                }
                break;
            default:
                break;
        }
    }

    getSmsEventType(status: SmsDeliveryStatus): EVENT_TYPES | undefined {
        switch (status) {
            case SmsDeliveryStatus.FAILED:
                return EVENT_TYPES.ERROR;
            case SmsDeliveryStatus.RESEND:
                return EVENT_TYPES.RESENDING_SMS;
            case SmsDeliveryStatus.DELIVERED:
                return EVENT_TYPES.SMS_DELIVERED;
            case SmsDeliveryStatus.UNKNOWN:
                return EVENT_TYPES.RESENDING_SMS;
            default:
                return undefined;
        }
    }

    async restoreSms(data: any, progressCb: ICallback) {
        this.emitProgress(new Event(EVENT_TYPES.SENDING_SMS, {}), progressCb);
        const {primary, secondary} = data.smsState || {};
        const primaryStatus = primary?.status;
        const secondaryStatus = secondary?.status;
        const timeOfLastMonitor = this._browserUtils.getFromSessionStorage('smsMonitorTime');
        const deltaTime = timeOfLastMonitor ? Date.now() - timeOfLastMonitor : 0;
        if (primaryStatus && secondaryStatus) {
            const primaryEventType = this.getSmsEventType(primaryStatus);
            let secondaryEventType = this.getSmsEventType(secondaryStatus);

            if (secondaryStatus === SmsDeliveryStatus.UNKNOWN) {
                secondaryEventType = EVENT_TYPES.ERROR;
            }
            if (primaryEventType) {
                this.emitProgress(new Event(primaryEventType, {}), progressCb);
            }
            if (secondaryEventType) {
                this.emitProgress(new Event(secondaryEventType, {}), progressCb);
                this.lastSmsStatus = secondaryStatus; // update last status to secondary
            }
            this.currentGateway = secondary.gateway;
            return;
        } else if (primaryStatus) {
            this.currentGateway = primary.gateway;
            const primaryEventType = this.getSmsEventType(primaryStatus);
            if (primaryEventType && primaryStatus !== SmsDeliveryStatus.UNKNOWN) {
                this.emitProgress(new Event(primaryEventType, {}), progressCb);
                this.lastSmsStatus = primaryStatus;
            }
            if (this.lastSmsStatus === SmsDeliveryStatus.DELIVERED) {
                return;
            }
        }
        const {smsPromptInitialTimeout} = this._smsSettings;
        this.smsTimeForMonitor = smsPromptInitialTimeout
            ? smsPromptInitialTimeout * 1000
            : SMS_DEFAULT_TIME_FOR_MONITOR;

        const shouldMonitor = deltaTime > 0 && this.smsTimeForMonitor - deltaTime > 0;

        if (shouldMonitor) {
            await this.monitorSmsDelivery(data.roomId, progressCb, deltaTime);
        } else if (
            this.lastSmsStatus === SmsDeliveryStatus.DELIVERED ||
            this.lastSmsStatus === SmsDeliveryStatus.RESEND
        ) {
            await this.monitorSmsDelivery(data.roomId, progressCb);
        }
    }

    private async _tryWhatsApp(
        smsData: ISmsData,
        progressCb: ICallback,
        allowSendByWhatsApp: boolean,
        smsServiceLogger: ISmsFlowLogger
    ): Promise<{
        messageId: string | null;
        success: boolean;
    }> {
        if (!allowSendByWhatsApp) {
            return {messageId: null, success: false};
        }

        const {messageId, success} = await this.sendWhatsAppTemplateMessage(smsData, progressCb);

        if (!success) {
            this._switchToSMSMessage(progressCb);

            return {messageId, success};
        }
        if (await this._doesWhatsAppDelivered(messageId)) {
            this.emitProgress(
                new Event(EVENT_TYPES.WHATSAPP_DELIVERED, {statusMessage: 'F.WHATSAPP_DELIVERED'}),
                progressCb
            );

            return {messageId, success: true};
        }
        smsServiceLogger.whatsappTimeoutError(smsData.countryCode + smsData.phone);

        this._switchToSMSMessage(progressCb);

        return {messageId, success: false};
    }

    async sendWhatsAppTemplateMessage(
        whatsAppData: any,
        progressCb: ICallback
    ): Promise<{
        messageId: string;
        success: boolean;
    }> {
        this.emitProgress(
            new Event(EVENT_TYPES.SENDING_WHATSAPP, {statusMessage: 'INVITE.CONTROLLER.SENDING_WHATSAPP'}),
            progressCb
        );

        try {
            const response = await this._smsService.sendWhatsAppTemplateMessage({
                toNumber: whatsAppData.countryCode + whatsAppData.phone,
                roomId: whatsAppData.roomId,
                link: whatsAppData.link
            });
            const messageId = response?.message_uuid || response?.data?.message_uuid || '';

            return {messageId, success: true};
        } catch (error) {
            return {messageId: '', success: false};
        }
    }

    dispose() {
        //TODO: Need to accomplish disposing correctly.
        //TODO: Currently intervals are not collected to "timeouts" so we cannot clear them.
        this._clearMonitorTimeout();
        this.currentGateway = '';
    }

    private emitProgress(event: Event, progressCb?: Function) {
        if (progressCb) {
            progressCb(event.asObject());
        }
    }

    _switchToSMSMessage(cb?: Function) {
        this.emitProgress(
            new Event(EVENT_TYPES.WHATSAPP_ERROR, {statusMessage: 'INVITE.CONTROLLER.SENDING_WHATSAPP_FAILED'}),
            cb
        );
        this.emitProgress(
            new Event(EVENT_TYPES.SWITCHING_TO_SMS, {statusMessage: 'INVITE.CONTROLLER.SWITCHING_TO_SMS'}),
            cb
        );
    }

    async _doesWhatsAppDelivered(messageId: string): Promise<boolean> {
        for (let i = 0; i < WHATSAPP_POLLING_DURING_PROMPT_ATTEMPTS; i++) {
            const now = Date.now();

            const statusResponse = await this._smsService.getWhatsAppTemplateMessageStatus(messageId);
            const status = statusResponse?.data?.status;

            if (this.whatsAppDeliveredStatus.includes(status)) {
                return true;
            }
            if (this.whatsAppFailedStatus.includes(status)) {
                return false;
            }
            if (i + 1 < WHATSAPP_POLLING_DURING_PROMPT_ATTEMPTS) {
                await sleep(WHATSAPP_POLLING_INTERVAL * 1000 - (Date.now() - now), 'polling interval');
            }
        }

        return false;
    }

    _updateSmsError(gateway: string, error: any, reason: string, cb?: ICallback) {
        let smsError: SmsError = new SmsError(error, SmsErrorLevel.ERROR);

        if (typeof error === 'object') {
            smsError = error;
        }

        this._smsServiceLogger.smsResolutionWarning(
            gateway,
            EventLogConstant.smsResolutionTypes.displayErrorMessage,
            reason || smsError.Message
        );

        this.emitProgress(new Event(EVENT_TYPES.ERROR, {smsError}), cb);
    }

    async _sendSms(smsData: ISmsData, cb: ICallback): Promise<any> {
        // Sending SMS...
        this.emitProgress(new Event(EVENT_TYPES.SENDING_SMS, {statusMessage: 'INVITE.CONTROLLER.SENDING_SMS'}), cb);
        this.currentGateway = '';
        const smsGatewayConfig = get(this._smsSettings, 'smsGatewayConfig');
        const primaryGateway = smsGatewayConfig && smsGatewayConfig.primary;
        let chosenPrimaryGatway = primaryGateway;
        const data: any = {
            phone: smsData.phone,
            roomId: smsData.roomId,
            link: smsData.link,
            chromeDetection: smsData.chromeDetection,
            cameraModeOnly: smsData.cameraModeOnly,
            showSmsPreview: smsData.showSmsPreview,
            gateway: primaryGateway,
            offline: smsData.offline,
            useNumberLookup: smsData.smsUseNumberLookup,
            countryCode: smsData.countryCode,
            audio: smsData.audio
        };

        if (smsData.startWithAgentType) {
            data.startWithAgentType = smsData.startWithAgentType;
        }
        return this._smsService
            .sendSms(data)
            .then((sms: any) => {
                const smsData = sms.data || sms;
                chosenPrimaryGatway = smsData.gateway || primaryGateway || Gateways.NEXMO;

                return {isPrimary: true, gateway: chosenPrimaryGatway, smsId: smsData._id};
            })
            .catch((error: any) => {
                if (error.status === 429) {
                    this.emitProgress(
                        new Event(EVENT_TYPES.ERROR, {customError: 'INVITE.CONTROLLER.SMS_CANNOT_BE_SENT_GENERAL'}),
                        cb
                    );
                } else if (error.status === 461) {
                    this.emitProgress(
                        new Event(EVENT_TYPES.ERROR, {
                            customError: 'INVITE.CONTROLLER.SMS_CANNOT_BE_SENT_INVALID_TELEPHONE'
                        }),
                        cb
                    );
                } else if (error.status === 462) {
                    this.emitProgress(
                        new Event(EVENT_TYPES.ERROR, {
                            customError: 'INVITE.CONTROLLER.SMS_CANNOT_BE_SENT_INVALID_NUMBER'
                        }),
                        cb
                    );
                } else {
                    this.emitProgress(
                        new Event(EVENT_TYPES.ERROR, {customError: 'INVITE.CONTROLLER.SMS_CANNOT_BE_SENT_TO_NUMBER'}),
                        cb
                    );
                }

                throw error;
            });
    }

    _smsProcessCompleted(roomId: string, gateway: string, clientConnected: boolean) {
        this._smsServiceLogger.smsResolutionInfo(
            gateway,
            EventLogConstant.smsResolutionTypes.moveToDashboard,
            clientConnected ? 'Client clicked on the SMS' : `${gateway} SMS delivered`
        );

        this._roomService.setReportedField(roomId, {
            key: 'gatewayUsedForInvite',
            value: `${gateway ? gateway : 'none'}`
        });
        return {success: true, clientConnected};
    }

    private _clearMonitorTimeout(): void {
        if (this.monitorDeliveryTimeout) {
            clearTimeout(this.monitorDeliveryTimeout);
            this.monitorDeliveryTimeout = null;
        }
    }

    private async _handleSmsStatus(
        status: string,
        roomId: string,
        progressCb: ICallback,
        smsGatewayConfig: any
    ): Promise<string | void> {
        switch (status) {
            case SmsDeliveryStatus.DELIVERED:
                if (!this.isClientLoaded && this.lastSmsStatus !== SmsDeliveryStatus.DELIVERED) {
                    const gateway =
                        this.lastSmsStatus === SmsDeliveryStatus.RESEND
                            ? smsGatewayConfig.secondary
                            : smsGatewayConfig.primary;
                    this._handleProgressUpdate(roomId, progressCb, gateway, false, SmsDeliveryStatus.DELIVERED);
                    this._browserUtils?.saveToSessionStorage('smsMonitorTime', Date.now());
                    await this.monitorSmsDelivery(roomId, progressCb);
                }
                break;

            case SmsDeliveryStatus.RESEND:
                if (!this.isClientLoaded && this.lastSmsStatus !== SmsDeliveryStatus.DELIVERED) {
                    if (this.lastSmsStatus !== SmsDeliveryStatus.RESEND) {
                        this._handleProgressUpdate(roomId, progressCb, '', false, SmsDeliveryStatus.RESEND);
                    }
                    this._browserUtils?.saveToSessionStorage('smsMonitorTime', Date.now());
                    await this.monitorSmsDelivery(roomId, progressCb);
                }
                break;

            case SmsDeliveryStatus.FAILED:
                if (this.lastSmsStatus !== SmsDeliveryStatus.FAILED) {
                    this._clearMonitorTimeout();
                    this._handleProgressUpdate(roomId, progressCb, '', false, SmsDeliveryStatus.FAILED);
                    return SmsDeliveryStatus.FAILED;
                }
                break;
        }

        return this.lastSmsStatus;
    }

    private async monitorSmsDelivery(roomId: string, progressCb: ICallback, deltaTime?: number): Promise<any> {
        const {smsGatewayConfig, smsPromptInitialTimeout} = this._smsSettings;
        this.smsTimeForMonitor = smsPromptInitialTimeout
            ? smsPromptInitialTimeout * 1000
            : SMS_DEFAULT_TIME_FOR_MONITOR;

        if (deltaTime && this.smsTimeForMonitor - deltaTime > 0) {
            this.smsTimeForMonitor = this.smsTimeForMonitor - deltaTime;
        }
        return new Promise((resolve, reject) => {
            this.monitorDeliveryTimeout = setTimeout(async () => {
                const originalGateway = this.currentGateway;

                try {
                    const response = await this._smsService.getDeliveryStatus(roomId);

                    if ((response as any).clientLoaded) {
                        this._handleProgressUpdate(roomId, progressCb, '', true);
                        this._clearMonitorTimeout();
                        this.isClientLoaded = true;
                        return resolve({success: true, clientConnected: true});
                    }

                    // Handle SMS delivery status
                    const {deliveryStatus} = response;
                    await this._handleSmsStatus(deliveryStatus, roomId, progressCb, smsGatewayConfig);

                    resolve({success: true});
                } catch (err) {
                    const failingGateway = smsGatewayConfig?.secondary || originalGateway;
                    // The SMS itself may well have been delivered - we only failed to read its
                    // status - so report a dedicated error instead of a "cannot be sent" one.
                    this._updateSmsError(
                        failingGateway,
                        new SmsError(SMS_STATUS_CHECK_FAILED_ERROR, SmsErrorLevel.INFO),
                        `monitorSmsDelivery failed: ${get(err, 'message', err)}`,
                        progressCb
                    );
                    resolve({success: false});
                }
            }, this.smsTimeForMonitor);
        });
    }
}
